/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import RouterService from '@ember/routing/router-service';
import { OPTION_EXISTING, OPTION_NEW, OPTION_MATCH_EMAIL, OPTION_EVERYTHING } from 'cio/components/import-csv/setup';
import ImportModel, { IMPORT_TYPES } from 'cio/models/import';
import { formatNumber } from 'cio/helpers/format-number';
import { formatNumberAndPluralize } from 'cio/helpers/format-number-and-pluralize';
import { IdentifierKey, ObjectIdentifierKey } from 'cio/utils/identifiers';
import pluralize from 'cio/utils/pluralize';
import { getImportTargetData } from 'cio/utils/imports';

interface SummaryMessageArgs {
  import: ImportModel;
  importType: string;
  isForSegment?: boolean;
}

export const hasNothingToImport = (record: ImportModel, importType: string) => {
  let newData = record.validateMeta?.newProfiles ?? -1;
  let existingData = record.validateMeta?.existingProfiles ?? -1;

  if (record.type === IMPORT_TYPES.object) {
    newData = record.validateMeta?.newObjects ?? -1;
    existingData = record.validateMeta?.existingObjects ?? -1;
  } else if (record.type === IMPORT_TYPES.relationship) {
    newData = record.validateMeta?.newRelationships ?? -1;
    existingData = record.validateMeta?.existingRelationships ?? -1;
  }

  return (
    record.get('hasAllInvalidRows') ||
    (importType === OPTION_NEW && newData === 0) ||
    (importType === OPTION_EXISTING && existingData === 0) ||
    (importType === OPTION_MATCH_EMAIL && existingData === 0) ||
    (importType === OPTION_EVERYTHING && newData === 0 && existingData === 0)
  );
};

const SEGMENT_EMAILS_NOT_FOUND_ERROR = 'File contains one or more emails that were not found.';
export default class SummaryMessage extends Component<SummaryMessageArgs> {
  @service router!: RouterService;

  get hasNothingToImport() {
    return hasNothingToImport(this.args.import, this.args.importType);
  }

  get summary() {
    let summary = {};
    let { import: record } = this.args;
    let { hasErrors, hasWarnings } = record;

    if (this.hasNothingToImport) {
      summary = this.nothingToImportSummary();
    } else if (!record.canImport) {
      summary = this.errorImportingSummary();
    } else if (hasWarnings && !hasErrors) {
      summary = this.warningsNoErrorsSummary();
    } else if (hasWarnings || hasErrors) {
      summary = this.warningsOrErrorsSummary();
    } else {
      summary = this.successSummary(record);
    }

    return summary;
  }

  get tryAgainRoute() {
    if (this.router.currentRouteName.includes('segments.segment.imports')) {
      return 'segments.segment.imports.csv.setup';
    }

    return 'imports.import.setup';
  }

  private successSummary(record: ImportModel) {
    let message = ['We'];
    // Segment imports don't use identifier, so we need to provide a fallback
    let { validateRules, type } = record;
    let { identifier } = validateRules;
    let { target, newTargetCount, existingTargetCount } = getImportTargetData(this.args.import);

    if (!identifier) {
      identifier = this.args.import.validateRules.matchEmail ? IdentifierKey.Email : IdentifierKey.Id;

      if (type === IMPORT_TYPES.object) {
        identifier = ObjectIdentifierKey.ObjectId;
      }
    }

    if (newTargetCount) {
      message.push(`found ${formatNumber([newTargetCount])} new ${pluralize(newTargetCount, target)}`);
    }

    if (newTargetCount && existingTargetCount) {
      message.push('and');
    }

    if (existingTargetCount) {
      message.push(
        `can ${record.isSegmentImport ? 'add' : 'update'} ${formatNumberAndPluralize([existingTargetCount, target])}`,
      );
    }

    message.push(!existingTargetCount ? 'to add' : `that matched on ${identifier}`);

    message.push('from your CSV.');

    return {
      message: message.join(' '),
      status: 'success',
      title: 'Success!',
    };
  }

  private warningsOrErrorsSummary() {
    let hasNewPeopleToSegmentError =
      (this.args.import.validateMeta?.errors?.includes(SEGMENT_EMAILS_NOT_FOUND_ERROR) ?? false) &&
      this.args.isForSegment;
    let importContext = this.args.isForSegment
      ? 'adding matched people to your segment'
      : 'your import without any changes';
    let importSideEffect = hasNewPeopleToSegmentError
      ? " but new people won't be added until added to the workspace first. <a rel='noopener noreferrer' target='_blank' href='https://customer.io/docs/identifying-people/'>Learn more</a>"
      : '.';
    return {
      message: `Some of your data did not pass our validation. You can choose to proceed with ${importContext}${importSideEffect}`,
      status: 'warning',
      title: 'Heads up!',
    };
  }

  private warningsNoErrorsSummary() {
    return {
      message:
        'Your data passed all our required validations, however there are optional validations for you to review before choosing to begin your import.',
      status: 'warning',
      title: 'Heads up!',
    };
  }

  private errorImportingSummary() {
    return {
      message: 'Something went wrong. There was a problem with your data.',
      status: 'error',
      title: 'Uh oh.',
    };
  }

  private nothingToImportSummary() {
    let { importType, import: record } = this.args;

    let verb = 'people';
    let singularVerb = 'person';
    if (record.objectType != null) {
      verb = record.objectType.name;
      singularVerb = record.objectType.singularName;
    }

    if (record.validateMeta == null) {
      return '';
    }

    let { rows, existingProfiles, newProfiles, invalidRows } = record.validateMeta;
    let messages = [];
    let tryAgain = false;

    messages.push("We haven't detected anything to import.");

    if ([OPTION_NEW, OPTION_EXISTING].includes(importType) && !this.args.isForSegment) {
      messages.push(
        `You opted to ${
          importType === OPTION_NEW ? `create new ${verb}` : `update existing ${verb}`
        } from your file, where matches are identified by ${record.identifier}.`,
      );
    }

    if (this.args.isForSegment) {
      messages.push(
        `We processed ${formatNumberAndPluralize([
          rows,
          'row',
        ])} and didn't find any matches to existing ${verb}. Double-check your CSV and errors in your file`,
      );
      tryAgain = true;
    } else {
      if ([OPTION_NEW, OPTION_EXISTING].includes(importType) && !record.get('hasAllInvalidRows')) {
        let reverseType = importType === 'new' ? 'existing' : 'new';
        let reverseTypeCount = importType === 'new' ? existingProfiles : newProfiles;

        messages.push(
          `We processed ${formatNumberAndPluralize([
            rows,
            'row',
          ])} and didn't find any matches to ${importType} ${verb}, but found ${reverseTypeCount} ${reverseType} ${pluralize(
            reverseTypeCount ?? 0,
            singularVerb,
          )}.`,
        );
      } else {
        messages.push(
          `We processed ${formatNumberAndPluralize([rows, 'row'])} and all ${pluralize(rows, 'row')} contained errors.`,
        );
      }

      if (record.get('hasAllInvalidRows')) {
        messages.push('Export the error file to address the issues');
        tryAgain = true;
      } else {
        messages.push(`Double-check your CSV${invalidRows ? ' and errors in your file' : ''}`);
        tryAgain = true;
      }
    }

    return {
      message: messages.join(' '),
      status: 'error',
      tryAgain,
    };
  }

  get docsUrl() {
    if (this.args.import.isObjectsImport || this.args.import.isRelationshipsImport) {
      return 'https://customer.io/docs/journeys/import-objects/#review';
    }
    return 'https://customer.io/docs/journeys/uploading-people/#review';
  }
}
